import React, { useState, useMemo } from 'react';
import { Card, CardBody, CardHeader, CardTitle, Button } from 'reactstrap';
import { Icon } from 'js/components';
import { Loading } from 'js/components';
import { PanelHeader } from 'js/components';
import { faPlug } from '@fortawesome/pro-regular-svg-icons';
import { Breadcrumbs } from 'js/components/Breadcrumbs';
import { useParams } from 'react-router-dom';
import { CONNECTOR_SERVICES } from 'js/data/connectors';
import { CONNECTOR_CONFIG_COMPONENTS } from './TeamConnectorConfiguration';

function EditConnectorConfiguration({
  team,
  currentConnector,
  currentConfiguration,
  onSaveConfiguration,
  onDeleteConfiguration,
  onCancel,
  isLoading,
}) {
  const { workspaceId, teamId } = useParams();

  const breadcrumbs = useMemo(
    () => [
      {
        label: 'Teams',
        to: `/w/${workspaceId}/teams`,
      },
      {
        label: team?.name,
        to: `/w/${workspaceId}/teams/${teamId}`,
      },
      {
        label: 'Settings',
        to: `/w/${workspaceId}/teams/${teamId}/settings`,
      },
    ],
    [workspaceId, teamId, team?.name]
  );

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Handle form submission
  const handleSubmit = async (configurationData) => {
    setIsSaving(true);
    try {
      await onSaveConfiguration(configurationData);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (
      window.confirm(
        'Are you sure you want to delete this connector configuration?'
      )
    ) {
      setIsDeleting(true);
      try {
        await onDeleteConfiguration();
      } finally {
        setIsDeleting(false);
      }
    }
  };

  // Get the appropriate configuration component
  const ConfigurationComponent = currentConnector
    ? CONNECTOR_CONFIG_COMPONENTS[currentConnector.type]
    : null;
  const connectorDisplayName = currentConnector
    ? CONNECTOR_SERVICES.find((cs) => cs.id === currentConnector.type)?.name ||
      currentConnector.type
    : null;

  if (isLoading) {
    return (
      <Card>
        <CardBody>
          <Loading />
        </CardBody>
      </Card>
    );
  }

  // Show configuration form
  if (currentConnector && ConfigurationComponent) {
    return (
      <div>
        <PanelHeader size="flex">
          <div className="panel-body-no-label px-4">
            <Breadcrumbs breadcrumbs={breadcrumbs} />
            <div>
              <h2>Configure {connectorDisplayName}</h2>
            </div>
          </div>
        </PanelHeader>
        <div className="content">
          {currentConnector && ConfigurationComponent ? (
            <Card>
              <CardBody>
                <ConfigurationComponent
                  connector={currentConnector}
                  existingConfiguration={currentConfiguration?.configuration}
                  onSubmit={handleSubmit}
                  onCancel={onCancel}
                  isSubmitting={isSaving}
                  isDeleting={isDeleting}
                  handleDelete={handleDelete}
                />
              </CardBody>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle tag="h5">Configuration Error</CardTitle>
              </CardHeader>
              <CardBody>
                <div className="media text-muted">
                  <Icon icon={faPlug} size="4x" fixedWidth className="mr-3" />
                  <div className="media-body">
                    <p className="text-muted mb-3">
                      {!currentConnector
                        ? 'Connector not found or not supported.'
                        : 'Configuration component not available for this connector type.'}
                    </p>
                    <Button color="outline-primary" onClick={onCancel}>
                      <Icon
                        icon={['far', 'arrow-left']}
                        className="btn-icon-left"
                      />
                      Back to Settings
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      </div>
    );
  }

  return null;
}

export default EditConnectorConfiguration;
